import { useState } from 'react';
import { toast } from 'sonner';

import { Select } from '@/components/atoms/select';
import { useFeatureFlag } from '@/components/providers/feature-flag-provider';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

interface Props {
  children: React.ReactNode;
  onSuccess?: () => void;
}

const titleOptions = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: ' ', label: '--' },
];

export default function CreateCustomerSheet({ children, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const isEnabled = useFeatureFlag('/customers/create');
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const mutation = api.customers.createCustomer.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Customer created successfully');
        setOpen(false);
        setFormData({
          title: '',
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
        });
        onSuccess?.();
        api.customers.searchCustomers.invalidateQueries();
      },
      meta: {
        toastError: 'Failed to create customer',
      },
    }
  );

  const [formData, setFormData] = useState({
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleTitleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.first_name || !formData.last_name) {
      toast.error('First name and last name are required');
      return;
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Build contacts array
    const contacts = [];
    if (formData.email) {
      contacts.push({
        type: 'EMAIL' as const,
        value: formData.email,
        primary: true,
        billing: false,
      });
    }
    if (formData.phone) {
      contacts.push({
        type: 'PHONE' as const,
        value: formData.phone,
        primary: true,
        billing: false,
      });
    }

    mutation.mutate({
      status: 'ACTIVE' as const,
      first_name: formData.first_name,
      last_name: formData.last_name,
      title: formData.title || null,
      company: null,
      office_id: officeId,
      commercial: false,
      contacts,
      preferences: {
        technicians: [],
        days: [],
        dates: [],
        enforce_days: false,
        confirmation: null,
      },
      locations: [],
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild disabled={!isEnabled}>
        {children}
      </SheetTrigger>
      <SheetContent
        className='w-full gap-2 overflow-y-auto pt-2 sm:max-w-lg md:top-4 md:right-4 md:h-[calc(100%-2rem)] md:rounded-lg'
        showClose
      >
        <SheetHeader className='flex flex-row items-center justify-between gap-2'>
          <div className='flex flex-col'>
            <SheetTitle className='text-lg'>Add New Customer</SheetTitle>
            <SheetDescription>Enter customer details below.</SheetDescription>
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit} className='space-y-4 px-4 pb-8'>
          <div className='flex items-center gap-4'>
            <div className='flex w-36 flex-col gap-2'>
              <Label htmlFor='title'>Title</Label>
              <Select
                name='title'
                placeholder='--'
                options={titleOptions}
                value={formData.title}
                onChange={handleTitleChange}
                className='w-full'
              />
            </div>

            <div className='flex w-full flex-col gap-2'>
              <Label htmlFor='first_name'>First Name *</Label>
              <Input
                id='first_name'
                type='text'
                placeholder='Enter first name'
                value={formData.first_name}
                onChange={handleInputChange('first_name')}
                required
              />
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='last_name'>Last Name *</Label>
            <Input
              id='last_name'
              type='text'
              placeholder='Enter last name'
              value={formData.last_name}
              onChange={handleInputChange('last_name')}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              type='email'
              placeholder='Enter email address'
              value={formData.email}
              onChange={handleInputChange('email')}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='phone'>Phone</Label>
            <Input
              id='phone'
              type='tel'
              placeholder='Enter phone number'
              value={formData.phone}
              onChange={handleInputChange('phone')}
            />
          </div>

          <div className='flex justify-end gap-2 pt-4'>
            <Button type='button' variant='outline' onClick={() => setOpen(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type='submit' disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating...' : 'Create Customer'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
